// src/api/stripeAdminService.js
// Admin service for Stripe management endpoints (superadmin only)

import { apiFetch } from "./http";
import { API_ENDPOINTS } from "../config";

export const stripeAdminService = {
  // ------------------------------------------------------------------
  // Stripe Config
  // ------------------------------------------------------------------

  async getConfig() {
    const response = await apiFetch(API_ENDPOINTS.ADMIN_STRIPE_CONFIG);
    if (!response.ok) throw new Error("Failed to get Stripe config");
    return response.json();
  },

  async updateConfig(config) {
    const response = await apiFetch(API_ENDPOINTS.ADMIN_STRIPE_CONFIG, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(config),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to update Stripe config");
    }
    return response.json();
  },

  // ------------------------------------------------------------------
  // Test Connection
  // ------------------------------------------------------------------

  async testConnection() {
    const response = await apiFetch(API_ENDPOINTS.ADMIN_STRIPE_TEST_CONNECTION, {
      method: "POST",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to test Stripe connection");
    }
    return response.json();
  },

  // ------------------------------------------------------------------
  // Stats
  // ------------------------------------------------------------------

  async getStats() {
    const response = await apiFetch(API_ENDPOINTS.ADMIN_STRIPE_STATS);
    if (!response.ok) throw new Error("Failed to get Stripe stats");
    return response.json();
  },

  // ------------------------------------------------------------------
  // Sync Products from Stripe
  // ------------------------------------------------------------------

  async syncProducts() {
    const response = await apiFetch(API_ENDPOINTS.ADMIN_STRIPE_SYNC, {
      method: "POST",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to sync products from Stripe");
    }
    return response.json();
  },

  // ------------------------------------------------------------------
  // Plans CRUD
  // ------------------------------------------------------------------

  async getPlans(includeInactive = false) {
    const params = includeInactive ? "?include_inactive=true" : "";
    const response = await apiFetch(`${API_ENDPOINTS.ADMIN_STRIPE_PLANS}${params}`);
    if (!response.ok) throw new Error("Failed to get plans");
    return response.json();
  },

  async createPlan(plan) {
    const response = await apiFetch(API_ENDPOINTS.ADMIN_STRIPE_PLANS, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(plan),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to create plan");
    }
    return response.json();
  },

  async updatePlan(planId, updates) {
    const response = await apiFetch(`${API_ENDPOINTS.ADMIN_STRIPE_PLANS}/${planId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to update plan");
    }
    return response.json();
  },

  async deactivatePlan(planId) {
    const response = await apiFetch(`${API_ENDPOINTS.ADMIN_STRIPE_PLANS}/${planId}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to deactivate plan");
    }
    return response.json();
  },

  // ------------------------------------------------------------------
  // Bulk Plan Actions
  // ------------------------------------------------------------------

  async bulkPlanAction(planIds, action) {
    const response = await apiFetch(API_ENDPOINTS.ADMIN_STRIPE_PLANS_BULK_ACTION, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ plan_ids: planIds, action }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to perform bulk action");
    }
    return response.json();
  },

  // ------------------------------------------------------------------
  // Coupons
  // ------------------------------------------------------------------

  async getCoupons() {
    const response = await apiFetch(API_ENDPOINTS.ADMIN_STRIPE_COUPONS);
    if (!response.ok) throw new Error("Failed to get coupons");
    return response.json();
  },

  async createCoupon(data) {
    const response = await apiFetch(API_ENDPOINTS.ADMIN_STRIPE_COUPONS, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to create coupon");
    }
    return response.json();
  },

  async deleteCoupon(couponId) {
    const response = await apiFetch(`${API_ENDPOINTS.ADMIN_STRIPE_COUPONS}/${couponId}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to delete coupon");
    }
    return response.json();
  },

  async getPromoCodes(couponId) {
    const response = await apiFetch(`${API_ENDPOINTS.ADMIN_STRIPE_COUPONS}/${couponId}/promo-codes`);
    if (!response.ok) throw new Error("Failed to get promo codes");
    return response.json();
  },

  // ------------------------------------------------------------------
  // Webhook Endpoints (auto-registration)
  // ------------------------------------------------------------------

  async getWebhookEndpoints() {
    const response = await apiFetch(API_ENDPOINTS.ADMIN_STRIPE_WEBHOOK_ENDPOINTS);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to get webhook endpoints");
    }
    return response.json();
  },

  async registerWebhookEndpoint(data = {}) {
    const response = await apiFetch(API_ENDPOINTS.ADMIN_STRIPE_WEBHOOK_ENDPOINTS, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to register webhook endpoint");
    }
    return response.json();
  },

  async deleteWebhookEndpoint(endpointId) {
    const response = await apiFetch(`${API_ENDPOINTS.ADMIN_STRIPE_WEBHOOK_ENDPOINTS}/${endpointId}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to delete webhook endpoint");
    }
    return response.json();
  },

  // ------------------------------------------------------------------
  // Webhook Events (audit log)
  // ------------------------------------------------------------------

  async getWebhookEvents(filters = {}) {
    const params = new URLSearchParams();
    if (filters.limit) params.set("limit", filters.limit);
    if (filters.event_type) params.set("event_type", filters.event_type);
    if (filters.event_status) params.set("event_status", filters.event_status);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const response = await apiFetch(`${API_ENDPOINTS.ADMIN_STRIPE_WEBHOOK_EVENTS}${qs}`);
    if (!response.ok) throw new Error("Failed to get webhook events");
    return response.json();
  },
};
